import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { debounce } from "lodash";
import { Formik, Field, Form, ErrorMessage } from "formik";
import {
  Button,
  Label,
  Segment,
  ModalActions,
  ModalContent,
  Modal,
  ModalHeader,
  Icon,
} from "semantic-ui-react";
import FetchingSpinner from "../../../FetchingSpinner";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import SelectMenu from "./SelectMenu";
import {
  isLoading,
  onSoftRemoveLeave,
  onUpdateLeaves,
  onAddLeaves,
  onGetLeaveTypes,
  onCreateTier,
  onUpdateTier,
  onDeleteTier,
} from "./leaveActions";
import { FormInput, TextAreaField } from "../../../../common";
import { Common } from "../../../../../utils";
import { ACCRUAL_START_DATE_OPTIONS, ACCRUAL_START_DATE_VALUES } from "../../../../../utils/constants";
import { useSubscriptionStatus } from "../../../../../hooks/useSubscriptionStatus";
import SubscriptionRestriction from "../../../subscriptionRestriction/SubscriptionRestriction";

import "../Settings.css";
import "./leavetype.css";

const LeaveType = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const leaveTypeStore = useSelector((state) => state.leaveTypeReducer);
  const { isSuspended } = useSubscriptionStatus();
  const history = useHistory();

  // Initialize leaveTypes with proper mapping of deducted to takeFromAllowance and accrual defaults
  const initialLeaveTypes = (leaveTypeStore.leaveTypes || []).map(leave => ({
    ...leave,
    takeFromAllowance: leave.takeFromAllowance !== undefined
      ? leave.takeFromAllowance
      : (leave.deducted !== undefined ? leave.deducted : false),
    deducted: leave.deducted !== undefined
      ? leave.deducted
      : (leave.takeFromAllowance !== undefined ? leave.takeFromAllowance : false),
    // Max Absent mapping - isMaxAbsent (boolean from DB), maxAbsent (numeric from DB)
    isMaxAbsent: leave.isMaxAbsent !== undefined ? leave.isMaxAbsent : false,
    maxAbsent: leave.maxAbsent !== undefined && leave.maxAbsent !== null ? leave.maxAbsent : 0,
    // Accrual defaults - use accrualFrequency directly (default to Annually if not set)
    accrualRate: leave.accrualRate || 0,
    accrualFrequency: leave.accrualFrequency !== undefined && leave.accrualFrequency !== null ? leave.accrualFrequency : 'Annually',
    accrualCap: leave.accrualCap !== undefined && leave.accrualCap !== null ? leave.accrualCap : (leave.maxAccrualBalance !== undefined ? leave.maxAccrualBalance : 0),
    enableProration: leave.enableProration !== undefined ? leave.enableProration : false,
    accrualStartType: leave.accrualStartType || ACCRUAL_START_DATE_VALUES.LEAVE_YEAR_START,
    tieredRates: leave.tieredRates || []
  }));
  const [leaveTypes, setLeaveTypes] = useState(initialLeaveTypes);
  const [expandedAccrualCards, setExpandedAccrualCards] = useState({});
  const isInitialMount = React.useRef(true);
  const [infoPopup, setInfoPopup] = useState({ type: null, index: null });
  const [shouldScrollToNew, setShouldScrollToNew] = useState(false);
  const newLeaveTypeRef = React.useRef(null);
  const lastAddedIndexRef = React.useRef(null);
  // Track unit selection (Days/Hours) for each leave type's accrual fields
  const [accrualRateUnits, setAccrualRateUnits] = useState({}); // { index: 'Days' | 'Hours' }
  const [accrualCapUnits, setAccrualCapUnits] = useState({}); // { index: 'Days' | 'Hours' }
  // Store original values to compare before saving
  const originalLeaveTypes = React.useRef({}); // { id: originalLeaveObject }

  // Redirect to billing if subscription is suspended
  useEffect(() => {
    if (isSuspended) {
      history.replace("/settings/company?tab=billing");
    }
  }, [isSuspended, history]);

  useEffect(() => {
    if (isSuspended) return;
    // Only sync from store on initial mount or when store is empty initially
    if (!leaveTypeStore.leaveTypes || leaveTypeStore.leaveTypes.length === 0) {
      return;
    }

    // On initial mount, load from store
    if (isInitialMount.current || leaveTypes.length === 0) {
      isInitialMount.current = false;
      const mappedLeaveTypes = (leaveTypeStore.leaveTypes || []).map((storeLeave, index) => ({
        ...storeLeave,
        // Map deducted to takeFromAllowance
        takeFromAllowance: storeLeave.takeFromAllowance !== undefined
          ? storeLeave.takeFromAllowance
          : (storeLeave.deducted !== undefined ? storeLeave.deducted : false),
        deducted: storeLeave.deducted !== undefined
          ? storeLeave.deducted
          : (storeLeave.takeFromAllowance !== undefined ? storeLeave.takeFromAllowance : false),
        // Max Absent mapping - isMaxAbsent (boolean from DB), maxAbsent (numeric from DB)
        isMaxAbsent: storeLeave.isMaxAbsent !== undefined ? storeLeave.isMaxAbsent : false,
        maxAbsent: storeLeave.maxAbsent !== undefined && storeLeave.maxAbsent !== null ? storeLeave.maxAbsent : 0,
        // Accrual defaults - use store values if available, otherwise defaults to Annually
        accrualRate: storeLeave.accrualRate !== undefined && storeLeave.accrualRate !== null ? storeLeave.accrualRate : 0,
        accrualFrequency: storeLeave.accrualFrequency !== undefined && storeLeave.accrualFrequency !== null ? storeLeave.accrualFrequency : 'Annually',
        accrualCap: storeLeave.accrualCap !== undefined && storeLeave.accrualCap !== null ? storeLeave.accrualCap : (storeLeave.maxAccrualBalance !== undefined ? storeLeave.maxAccrualBalance : 0),
        enableProration: storeLeave.enableProration !== undefined ? storeLeave.enableProration : false,
        accrualStartType: storeLeave.accrualStartType || ACCRUAL_START_DATE_VALUES.LEAVE_YEAR_START,
        tieredRates: storeLeave.tieredRates || []
      }));
      setLeaveTypes(mappedLeaveTypes);

      // Initialize division units from backend data
      // Backend returns integer enum: 0 = Days, 1 = Hours
      const initialRateUnits = {};
      const initialCapUnits = {};
      (leaveTypeStore.leaveTypes || []).forEach((storeLeave, index) => {
        // Read from storeLeave directly to get the division values from backend
        if (storeLeave.accrualRateDivision !== undefined && storeLeave.accrualRateDivision !== null) {
          // Handle both integer (0/1) and string ('Days'/'Hours') values
          initialRateUnits[index] = (storeLeave.accrualRateDivision === 1 || storeLeave.accrualRateDivision === 'Hours' || storeLeave.accrualRateDivision === 'hours')
            ? 'Hours'
            : 'Days';
        }
        if (storeLeave.accrualCapDivision !== undefined && storeLeave.accrualCapDivision !== null) {
          // Handle both integer (0/1) and string ('Days'/'Hours') values
          initialCapUnits[index] = (storeLeave.accrualCapDivision === 1 || storeLeave.accrualCapDivision === 'Hours' || storeLeave.accrualCapDivision === 'hours')
            ? 'Hours'
            : 'Days';
        }
      });
      if (Object.keys(initialRateUnits).length > 0) {
        setAccrualRateUnits(prev => ({ ...prev, ...initialRateUnits }));
      }
      if (Object.keys(initialCapUnits).length > 0) {
        setAccrualCapUnits(prev => ({ ...prev, ...initialCapUnits }));
      }

      // Store original values for comparison
      mappedLeaveTypes.forEach(leave => {
        if (leave.id) {
          originalLeaveTypes.current[leave.id] = JSON.parse(JSON.stringify(leave));
        }
      });
      return;
    }

    // After initial load, only merge in new leave types or updated ones (by ID)
    // Preserve local state for existing leave types to prevent overwriting user edits
    const currentIds = new Set(leaveTypes.map(lt => lt.id));
    const storeIds = new Set(leaveTypeStore.leaveTypes.map(lt => lt.id));

    // Check if there are new leave types in store
    const hasNewLeaveTypes = leaveTypeStore.leaveTypes.some(storeLeave => !currentIds.has(storeLeave.id));

    if (hasNewLeaveTypes) {
      // Merge: keep existing local state, add new ones from store
      const existingLeaveTypes = leaveTypes.filter(lt => storeIds.has(lt.id));
      const newLeaveTypes = leaveTypeStore.leaveTypes
        .filter(storeLeave => !currentIds.has(storeLeave.id))
        .map((storeLeave, relativeIndex) => {
          const actualIndex = existingLeaveTypes.length + relativeIndex;
          // Initialize division units from backend data for new leave types
          // Backend returns integer enum: 0 = Days, 1 = Hours
          if (storeLeave.accrualRateDivision !== undefined && storeLeave.accrualRateDivision !== null) {
            setAccrualRateUnits(prev => ({
              ...prev,
              [actualIndex]: (storeLeave.accrualRateDivision === 1 || storeLeave.accrualRateDivision === 'Hours' || storeLeave.accrualRateDivision === 'hours')
                ? 'Hours'
                : 'Days'
            }));
          }
          if (storeLeave.accrualCapDivision !== undefined && storeLeave.accrualCapDivision !== null) {
            setAccrualCapUnits(prev => ({
              ...prev,
              [actualIndex]: (storeLeave.accrualCapDivision === 1 || storeLeave.accrualCapDivision === 'Hours' || storeLeave.accrualCapDivision === 'hours')
                ? 'Hours'
                : 'Days'
            }));
          }
          return {
            ...storeLeave,
            takeFromAllowance: storeLeave.takeFromAllowance !== undefined
              ? storeLeave.takeFromAllowance
              : (storeLeave.deducted !== undefined ? storeLeave.deducted : false),
            deducted: storeLeave.deducted !== undefined
              ? storeLeave.deducted
              : (storeLeave.takeFromAllowance !== undefined ? storeLeave.takeFromAllowance : false),
            accrualRate: storeLeave.accrualRate !== undefined && storeLeave.accrualRate !== null ? storeLeave.accrualRate : 0,
            accrualFrequency: storeLeave.accrualFrequency !== undefined && storeLeave.accrualFrequency !== null ? storeLeave.accrualFrequency : 'Annually',
            accrualCap: storeLeave.accrualCap !== undefined && storeLeave.accrualCap !== null ? storeLeave.accrualCap : (storeLeave.maxAccrualBalance !== undefined ? storeLeave.maxAccrualBalance : 0),
            enableProration: storeLeave.enableProration !== undefined ? storeLeave.enableProration : false,
            // Max Absent mapping - isMaxAbsent (boolean from DB), maxAbsent (numeric from DB)
            isMaxAbsent: storeLeave.isMaxAbsent !== undefined ? storeLeave.isMaxAbsent : false,
            maxAbsent: storeLeave.maxAbsent !== undefined && storeLeave.maxAbsent !== null ? storeLeave.maxAbsent : 0,
            accrualStartType: storeLeave.accrualStartType || ACCRUAL_START_DATE_VALUES.LEAVE_YEAR_START,
            tieredRates: storeLeave.tieredRates || [],
            enableAccrual: storeLeave.enableAccrual !== undefined ? storeLeave.enableAccrual : (storeLeave.accrualFrequency && storeLeave.accrualFrequency !== null)
          };
        });
      setLeaveTypes([...existingLeaveTypes, ...newLeaveTypes]);
      // Store original values for new leave types
      newLeaveTypes.forEach(leave => {
        if (leave.id) {
          originalLeaveTypes.current[leave.id] = JSON.parse(JSON.stringify(leave));
        }
      });
    } else {
      // Update original values for existing leave types that were updated in the store
      // This happens after a successful API save
      leaveTypeStore.leaveTypes.forEach(storeLeave => {
        if (storeLeave.id && currentIds.has(storeLeave.id)) {
          // Map the store leave to match our local structure
          const mappedLeave = {
            ...storeLeave,
            takeFromAllowance: storeLeave.takeFromAllowance !== undefined
              ? storeLeave.takeFromAllowance
              : (storeLeave.deducted !== undefined ? storeLeave.deducted : false),
            deducted: storeLeave.deducted !== undefined
              ? storeLeave.deducted
              : (storeLeave.takeFromAllowance !== undefined ? storeLeave.takeFromAllowance : false),
            isMaxAbsent: storeLeave.isMaxAbsent !== undefined ? storeLeave.isMaxAbsent : false,
            maxAbsent: storeLeave.maxAbsent !== undefined && storeLeave.maxAbsent !== null ? storeLeave.maxAbsent : 0,
            accrualRate: storeLeave.accrualRate !== undefined && storeLeave.accrualRate !== null ? storeLeave.accrualRate : 0,
            accrualFrequency: storeLeave.accrualFrequency !== undefined && storeLeave.accrualFrequency !== null ? storeLeave.accrualFrequency : 'Annually',
            accrualCap: storeLeave.accrualCap !== undefined && storeLeave.accrualCap !== null ? storeLeave.accrualCap : (storeLeave.maxAccrualBalance !== undefined ? storeLeave.maxAccrualBalance : 0),
            enableProration: storeLeave.enableProration !== undefined ? storeLeave.enableProration : false,
            accrualStartType: storeLeave.accrualStartType || ACCRUAL_START_DATE_VALUES.LEAVE_YEAR_START,
            tieredRates: storeLeave.tieredRates || [],
            enableAccrual: storeLeave.enableAccrual !== undefined ? storeLeave.enableAccrual : (storeLeave.accrualFrequency && storeLeave.accrualFrequency !== null)
          };
          // Update original values to reflect the saved state
          originalLeaveTypes.current[storeLeave.id] = JSON.parse(JSON.stringify(mappedLeave));
        }
      });
    }
    // Otherwise, preserve local state - don't overwrite user's edits
  }, [leaveTypeStore.leaveTypes, isSuspended]);

  useEffect(() => {
    if (!isSuspended) {
      dispatch(onGetLeaveTypes());
    }
  }, [dispatch, isSuspended]);

  // Initialize Bootstrap tooltips
  useEffect(() => {
    if (isSuspended) return;
    if (typeof window !== 'undefined' && window.bootstrap) {
      const tooltipTriggerList = document.querySelectorAll(
        '[data-bs-toggle="tooltip"]'
      );
      const tooltipList = [...tooltipTriggerList].map(
        (tooltipTriggerEl) => new window.bootstrap.Tooltip(tooltipTriggerEl)
      );

      // Cleanup function to destroy tooltips when component unmounts or leaveTypes change
      return () => {
        tooltipList.forEach(tooltip => {
          if (tooltip && typeof tooltip.dispose === 'function') {
            tooltip.dispose();
          }
        });
      };
    }
  }, [leaveTypes, isSuspended]);

  // Scroll to newly added leave type
  useEffect(() => {
    if (isSuspended) return;
    if (shouldScrollToNew && lastAddedIndexRef.current !== null) {
      // Use setTimeout to ensure the DOM has updated
      setTimeout(() => {
        if (newLeaveTypeRef.current) {
          newLeaveTypeRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          setShouldScrollToNew(false);
          lastAddedIndexRef.current = null;
        }
      }, 150);
    }
  }, [shouldScrollToNew, isSuspended]);

  // Show restriction message if subscription is suspended
  if (isSuspended) {
    return (
      <div style={{ padding: "20px" }}>
        <SubscriptionRestriction actionKey="app.subscriptionRestriction.actionAccessSettings" />
      </div>
    );
  }

  // Generate a random light color (not too dark)
  const generateRandomColor = () => {
    // Generate HSL color with:
    // - Hue: random (0-360)
    // - Saturation: 40-70% (vibrant but not too saturated for lighter colors)
    // - Lightness: 60-80% (lighter colors, not too dark)
    const hue = Math.floor(Math.random() * 360);
    const saturation = Math.floor(Math.random() * 30) + 40; // 40-70%
    const lightness = Math.floor(Math.random() * 20) + 60; // 60-80%

    // Convert HSL to RGB
    const h = hue / 360;
    const s = saturation / 100;
    const l = lightness / 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((h * 6) % 2 - 1));
    const m = l - c / 2;

    let r, g, b;

    if (h < 1 / 6) {
      r = c; g = x; b = 0;
    } else if (h < 2 / 6) {
      r = x; g = c; b = 0;
    } else if (h < 3 / 6) {
      r = 0; g = c; b = x;
    } else if (h < 4 / 6) {
      r = 0; g = x; b = c;
    } else if (h < 5 / 6) {
      r = x; g = 0; b = c;
    } else {
      r = c; g = 0; b = x;
    }

    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);

    // Convert to hex
    const toHex = (n) => {
      const hex = n.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  const handleChange = (index, field, value) => {
    const updated = [...leaveTypes];
    updated[index][field] = value;
    setLeaveTypes(updated);
  };

  const toggleAccrualSection = (index) => {
    setExpandedAccrualCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Helper function to convert days to hours (assuming 8 hours per day)
  const daysToHours = (days) => {
    if (!days || days === 0) return 0;
    return (days * 8).toFixed(1);
  };

  // Helper function to convert hours to days (assuming 8 hours per day)
  const hoursToDays = (hours) => {
    if (!hours || hours === 0) return 0;
    return (hours / 8).toFixed(2);
  };

  // Helper function to get display name from accrual frequency
  const getFrequencyDisplayName = (frequency) => {
    const displayMap = {
      'Monthly': t("app.leaveTypes.frequencyMonthly"),
      'BiWeekly': t("app.leaveTypes.frequencyBiWeekly"),
      'Bi-weekly': t("app.leaveTypes.frequencyBiWeekly"),
      'Weekly': t("app.leaveTypes.frequencyWeekly"),
      'Quarterly': t("app.leaveTypes.frequencyQuarterly"),
      'Annually': t("app.leaveTypes.frontLoadedAnnualGrant"),
      'Tiered': t("app.leaveTypes.frequencyTiered"),
      null: t("app.leaveTypes.frequencyNone"),
      undefined: t("app.leaveTypes.frequencyNone")
    };
    return displayMap[frequency] || frequency || t("app.leaveTypes.frequencyNone");
  };

  const calculateAnnualAccrual = (accrualFrequency, accrualRate) => {
    if (!accrualFrequency || accrualFrequency === null || !accrualRate || accrualRate <= 0) return 0;

    switch (accrualFrequency) {
      case 'Annually':
        return accrualRate;
      case 'Monthly':
        return accrualRate * 12;
      case 'BiWeekly':
      case 'Bi-weekly':
        return accrualRate * 26;
      case 'Weekly':
        return accrualRate * 52;
      case 'Quarterly':
        return accrualRate * 4;
      case 'Tiered':
        // For tiered, show average or max tier rate
        return accrualRate; // This will be handled differently in the UI
      default:
        return accrualRate;
    }
  };

  const validateAccrualSettings = (leave) => {
    const errors = [];

    if (leave.accrualFrequency && leave.accrualFrequency !== null) {
      // Only require accrual rate for frequencies that need it (not Tiered or Annually)
      if (leave.accrualFrequency !== 'Tiered' && leave.accrualFrequency !== 'Annually') {
        if (!leave.accrualRate || leave.accrualRate <= 0) {
          errors.push('Accrual rate must be greater than 0');
        }
      }

      if (leave.accrualCap !== undefined && leave.accrualCap < 0) {
        errors.push('Accrual cap cannot be negative');
      }

      if (leave.accrualFrequency === 'Tiered') {
        if (!leave.tieredRates || leave.tieredRates.length === 0) {
          // Don't show error during initial selection, only when saving with incomplete data
          // This allows user to configure tiers after selecting Tiered method
        } else {
          leave.tieredRates.forEach((tier, index) => {
            if (tier.years < 0) {
              errors.push(`Tier ${index + 1}: Years cannot be negative`);
            }
            if (tier.days < 0) {
              errors.push(`Tier ${index + 1}: Days cannot be negative`);
            }
          });
        }
      }
    }

    return errors;
  };

  const toggleActiveStatus = (id, index, active) => {
    const updated = [...leaveTypes];
    updated[index].active = active;
    setLeaveTypes(updated);

    // Save to server if the leave type has an ID
    if (id) {
      saveToServer(updated[index], index);
    }
  };

  // Helper function to check if any fields have changed
  const hasChanges = (current, original) => {
    if (!original) return true; // New leave type, always save

    // Helper to normalize values for comparison
    const normalizeValue = (val) => {
      if (val === null || val === undefined) return null;
      if (typeof val === 'number') return val;
      if (typeof val === 'string') return val.trim();
      if (typeof val === 'boolean') return val;
      return val;
    };

    // Compare all relevant fields
    const fieldsToCompare = [
      'name',
      'color',
      'icon',
      'defaultLeaveCount',
      'requiresApproval',
      'takeFromAllowance',
      'deducted',
      'active',
      'isMaxAbsent',
      'maxAbsent',
      'accrualFrequency',
      'accrualRate',
      'accrualCap',
      'enableProration',
      'accrualStartType',
      'enableAccrual'
    ];

    // Check if any field has changed
    for (const field of fieldsToCompare) {
      const currentVal = normalizeValue(current[field]);
      const originalVal = normalizeValue(original[field]);

      // Special handling for numeric fields (handle 0 vs null vs undefined)
      if (field === 'defaultLeaveCount' || field === 'maxAbsent' || field === 'accrualRate' || field === 'accrualCap') {
        const currentNum = currentVal !== null && currentVal !== undefined ? parseFloat(currentVal) : 0;
        const originalNum = originalVal !== null && originalVal !== undefined ? parseFloat(originalVal) : 0;
        if (currentNum !== originalNum) {
          return true;
        }
      } else if (currentVal !== originalVal) {
        return true;
      }
    }

    // Compare tieredRates (deep comparison)
    const currentTiers = JSON.stringify((current.tieredRates || []).map(t => ({
      id: t.id,
      years: t.years,
      days: t.days,
      hours: t.hours,
      tierOrder: t.tierOrder,
      active: t.active
    })).sort((a, b) => a.tierOrder - b.tierOrder));

    const originalTiers = JSON.stringify((original.tieredRates || []).map(t => ({
      id: t.id,
      years: t.years,
      days: t.days,
      hours: t.hours,
      tierOrder: t.tierOrder,
      active: t.active
    })).sort((a, b) => a.tierOrder - b.tierOrder));

    if (currentTiers !== originalTiers) {
      return true;
    }

    return false;
  };

  const saveToServer = async (leave, index = null, forceSave = false) => {
    // If index not provided, find it from leaveTypes array
    if (index === null) {
      index = leaveTypes.findIndex(lt => lt.id === leave.id || lt === leave);
    }
    if (!leave.name?.trim()) return;

    // Check if any fields have changed
    // If it's an existing leave type and no fields changed, skip the API call
    // Unless forceSave is true (e.g., when division units change)
    if (!forceSave && leave.id && originalLeaveTypes.current[leave.id]) {
      const original = originalLeaveTypes.current[leave.id];
      if (!hasChanges(leave, original)) {
        // No changes detected, don't make API call
        return;
      }
    }

    // If Allowance is disabled (non-deductible), disable accrual
    if (!leave.takeFromAllowance && !leave.deducted) {
      leave.enableAccrual = false;
      leave.accrualFrequency = null;
      leave.accrualRate = 0;
      leave.accrualCap = 0;
      leave.tieredRates = [];
    }

    // Validate accrual settings before saving (only for complete configurations)
    // Allow saving during frequency selection to update the UI
    if (leave.accrualFrequency && leave.accrualFrequency !== null && leave.enableAccrual) {
      const validationErrors = validateAccrualSettings(leave);
      // Only block save if there are critical validation errors
      // Allow saving when user is still configuring (e.g., just selected frequency, no rate yet)
      const blockingErrors = validationErrors.filter(err => {
        // Don't block if user just selected a frequency and hasn't entered rate yet
        if (err.includes('Accrual rate must be greater than 0') &&
          (leave.accrualFrequency === 'Tiered' || leave.accrualFrequency === 'Annually')) {
          return false; // These frequencies don't need accrualRate
        }
        // Block on negative values or missing tiered rates when frequency is Tiered
        return err.includes('cannot be negative') ||
          (leave.accrualFrequency === 'Tiered' && err.includes('tier') && leave.tieredRates && leave.tieredRates.length > 0);
      });

      if (blockingErrors.length > 0) {
        Common.showErrorMessage(blockingErrors.join(', '));
        return;
      }
    }

    // Get division values - check leave object first (if passed directly), then state, then default
    // C# enum LeaveDivision: Days = 0, Hours = 1
    let accrualRateDivision;
    let accrualCapDivision;

    // If division values are already in the leave object (passed directly), use them
    if (leave.accrualRateDivision !== undefined && leave.accrualRateDivision !== null) {
      accrualRateDivision = (typeof leave.accrualRateDivision === 'number')
        ? leave.accrualRateDivision  // Already an integer (0 or 1)
        : ((leave.accrualRateDivision === 'Hours' || leave.accrualRateDivision === 'hours') ? 1 : 0);
    } else {
      // Otherwise, read from state
      const accrualRateDivisionValue = (index !== null && index !== undefined && accrualRateUnits[index])
        ? accrualRateUnits[index]
        : 'Days';
      accrualRateDivision = (accrualRateDivisionValue === 'Hours' || accrualRateDivisionValue === 'hours')
        ? 1  // Hours
        : 0; // Days
    }

    if (leave.accrualCapDivision !== undefined && leave.accrualCapDivision !== null) {
      accrualCapDivision = (typeof leave.accrualCapDivision === 'number')
        ? leave.accrualCapDivision  // Already an integer (0 or 1)
        : ((leave.accrualCapDivision === 'Hours' || leave.accrualCapDivision === 'hours') ? 1 : 0);
    } else {
      // Otherwise, read from state
      const accrualCapDivisionValue = (index !== null && index !== undefined && accrualCapUnits[index])
        ? accrualCapUnits[index]
        : 'Days';
      accrualCapDivision = (accrualCapDivisionValue === 'Hours' || accrualCapDivisionValue === 'hours')
        ? 1  // Hours
        : 0; // Days
    }

    // Add division fields to leave object
    const leaveWithDivisions = {
      ...leave,
      accrualRateDivision: accrualRateDivision,
      accrualCapDivision: accrualCapDivision
    };

    try {
      if (leave.id) {
        // For existing leave types, update
        dispatch(onUpdateLeaves(leaveWithDivisions));
        // Note: Original values will be updated in useEffect when store updates
      } else {
        // For new leave types, create
        dispatch(onAddLeaves(leaveWithDivisions));
      }
    } catch (error) {
    }
  };

  // Save tier to server using the new tier API
  const saveTierToServer = async (leaveTypeId, tier, tierIndex) => {
    if (!leaveTypeId || !tier) return;

    // Only save tiers that have at least one meaningful value
    const hasValue = (tier.years && tier.years > 0) ||
      (tier.days && tier.days > 0) ||
      (tier.hours && tier.hours > 0);

    // If tier has no values and no ID, don't save it yet
    if (!hasValue && !tier.id) {
      return;
    }

    const tierData = {
      years: tier.years || 0,
      days: tier.days || 0,
      hours: tier.hours || 0,
      tierOrder: tierIndex + 1, // 1-based index
      active: tier.active !== undefined ? tier.active : true
    };

    try {
      // If tier has an ID, it's an update; otherwise, it's a create
      if (tier.id) {
        // Only update if tier has values, otherwise skip
        if (hasValue) {
          await dispatch(onUpdateTier(tier.id, { ...tierData, id: tier.id }));
        }
      } else {
        // Only create if tier has values
        if (hasValue) {
          const result = await dispatch(onCreateTier(leaveTypeId, tierData));
          if (result && result.success && result.data) {
            // Update the tier with the ID from the response
            const updated = [...leaveTypes];
            const leaveIndex = leaveTypes.findIndex(lt => lt.id === leaveTypeId);
            if (leaveIndex !== -1 && updated[leaveIndex].tieredRates && updated[leaveIndex].tieredRates[tierIndex]) {
              updated[leaveIndex].tieredRates[tierIndex] = { ...tier, id: result.data.id };
              setLeaveTypes(updated);
            }
          }
        }
      }
    } catch (error) {
      // If update fails with "not found", it means the tier was deleted or never created
      // Remove the ID from local state so it can be created fresh
      if (error?.response?.data?.message?.includes('not found') || error?.message?.includes('not found')) {
        const updated = [...leaveTypes];
        const leaveIndex = leaveTypes.findIndex(lt => lt.id === leaveTypeId);
        if (leaveIndex !== -1 && updated[leaveIndex].tieredRates && updated[leaveIndex].tieredRates[tierIndex]) {
          const { id, ...tierWithoutId } = updated[leaveIndex].tieredRates[tierIndex];
          updated[leaveIndex].tieredRates[tierIndex] = tierWithoutId;
          setLeaveTypes(updated);
        }
      }
    }
  };

  const renderLeaves = (item, index) => {
    const isNewItem = shouldScrollToNew && lastAddedIndexRef.current === index && !item.id;
    return (
      <div
        key={item.id || index}
        ref={isNewItem ? newLeaveTypeRef : null}
        className={`leave-type-modern-card ${item.active === false ? 'disabled-card' : ''}`}
        style={{
          background: item.active === false
            ? "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)"
            : "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
          border: `2px solid ${item.active === false ? '#dee2e6' : item.color + '20'}`,
          opacity: item.active === false ? 0.6 : 1,
        }}
      >
        {/* Card Header */}
        <div className="card-header-section">
          <div className="header-left">
            {/* Color Indicator */}
            <div className="color-indicator-wrapper">
              <div
                className="color-indicator"
                style={{
                  backgroundColor: item.color,
                }}
                title={t("app.leaveTypes.clickToChangeColor")}
              >
                <input
                  type="color"
                  value={item.color}
                  onChange={(e) => handleChange(index, "color", e.target.value)}
                  onBlur={(e) => {
                    saveToServer(leaveTypes[index]);
                  }}
                  className="color-picker"
                  title={t("app.leaveTypes.chooseColor")}
                  disabled={item.active === false}
                />
              </div>
            </div>

            {/* Leave Name */}
            <div className="leave-name-section">
              <input
                type="text"
                className="leave-name-input"
                placeholder={t("app.leaveTypes.enterLeaveTypeName")}
                value={item.name}
                disabled={item.active === false}
                onChange={(e) => handleChange(index, "name", e.target.value)}
                onBlur={() => {
                  if (item.name?.trim()) {
                    saveToServer(leaveTypes[index]);
                  }
                }}
                style={{
                  color: item.color,
                }}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="header-actions">
            {!item.isDefaultLeave && index !== 0 ? (
              <div className="toggle-switch-container interactive-toggle">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={item.active !== false}
                    onChange={(e) => toggleActiveStatus(item.id, index, e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <span className="toggle-label">
                  {item.active !== false ? t("app.leaveTypes.active") : t("app.leaveTypes.disabled")}
                </span>
              </div>
            ) : (
              <span className="status-badge primary">
                <Icon name="shield alternate" className="m-0 mb-2" />
                {index === 0 ? t("app.leaveTypes.primary") : t("app.leaveTypes.default")}
              </span>
            )}
          </div>
        </div>

        {/* Card Body */}
        <div className={`card-body-section ${item.active === false ? 'disabled-content' : ''}`}>
          {/* Settings Row */}
          <div className="toggle-switches d-flex flex-wrap align-items-end mt-3">
            {/* Icon */}
            <div>
              <label className="settings-label">{t("app.leaveTypes.icon")}</label>
              <SelectMenu
                value={item.icon || item.selectOption || "no-icon"}
                onChange={(val) => {
                  handleChange(index, "icon", val);
                  handleChange(index, "selectOption", val); // Keep for backward compatibility
                  if (item.id) {
                    saveToServer(leaveTypes[index]);
                  }
                }}
                disabled={item.active === false}
              />
            </div>

            {/* Default Leave Count - Only show when Allowance is enabled */}
            {(item.takeFromAllowance === true || item.deducted === true) && (
              <div>
                <label className="settings-label">
                  {t("app.leaveTypes.defaultDays")} {item.defaultLeaveCount > 0 && (
                    <span className="text-muted" style={{ fontSize: "0.85em", fontWeight: "normal" }}>
                      ({t("app.leaveTypes.hoursInParens", { hours: daysToHours(item.defaultLeaveCount) })})
                    </span>
                  )}
                </label>
                <div className="count-input-group h40 w90">
                  <input
                    type="number"
                    className="count-input"
                    placeholder="0"
                    value={item.defaultLeaveCount || 0}
                    onChange={(e) =>
                      handleChange(
                        index,
                        "defaultLeaveCount",
                        Number(e.target.value)
                      )
                    }
                    onBlur={() => {
                      if (item.id) {
                        saveToServer(leaveTypes[index]);
                      }
                    }}
                    min="0"
                    step="1"
                    disabled={item.active === false}
                  />
                </div>
              </div>
            )}

            {/* Take from Allowance */}
            <div className="toggle-switch-item">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={item.takeFromAllowance !== undefined ? item.takeFromAllowance : (item.deducted !== undefined ? item.deducted : false)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    const updated = [...leaveTypes];
                    updated[index].takeFromAllowance = checked;
                    updated[index].deducted = checked;

                    // If Allowance is disabled (non-deductible), disable accrual
                    if (!checked) {
                      updated[index].enableAccrual = false;
                      updated[index].accrualFrequency = null;
                      updated[index].accrualRate = 0;
                      updated[index].accrualCap = 0;
                      updated[index].tieredRates = [];
                    } else {
                      // If Allowance is enabled (deductible), set default accrual
                      if (!updated[index].accrualFrequency) {
                        updated[index].accrualFrequency = 'Annually';
                        // When frequency is Annually, enableAccrual should be false
                        updated[index].enableAccrual = false;
                      } else if (updated[index].accrualFrequency === 'Annually') {
                        // If frequency is already Annually, set enableAccrual to false
                        updated[index].enableAccrual = false;
                      } else {
                        // For other frequencies, enable accrual
                        updated[index].enableAccrual = true;
                      }
                    }

                    setLeaveTypes(updated);

                    // Save to server if the leave type has an ID
                    if (updated[index].id) {
                      saveToServer(updated[index]);
                    }
                  }}
                  onBlur={() => {
                    if (item.id) {
                      saveToServer(leaveTypes[index]);
                    }
                  }}
                  disabled={item.active === false}
                  title={t("app.leaveTypes.allowanceTooltip")}
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                />
                <span className="toggle-slider"></span>
              </label>
              <span
                className="toggle-label"
                title={t("app.leaveTypes.allowanceTooltip")}
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                style={{ cursor: 'help' }}
              >
                {t("app.leaveTypes.allowance")}
              </span>
            </div>

            {/* Requires Approval */}
            <div className="toggle-switch-item">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={item.requiresApproval}
                  onChange={(e) =>
                    handleChange(index, "requiresApproval", e.target.checked)
                  }
                  onBlur={() => {
                    if (item.id) {
                      saveToServer(leaveTypes[index]);
                    }
                  }}
                  disabled={item.active === false}
                  title={t("app.leaveTypes.requiresApproval")}
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                />
                <span className="toggle-slider"></span>
              </label>
              <span
                className="toggle-label"
                title={t("app.leaveTypes.requiresApproval")}
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                style={{ cursor: 'help' }}
              >
                {t("app.leaveTypes.approval")}
              </span>
            </div>

            {/* Max Absent */}
            <div className="toggle-switch-item">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={item.isMaxAbsent}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    const updated = [...leaveTypes];
                    updated[index].isMaxAbsent = checked;
                    // Clear maxAbsent value when toggle is turned off
                    if (!checked) {
                      updated[index].maxAbsent = 0;
                    }
                    setLeaveTypes(updated);
                    if (updated[index].id) {
                      saveToServer(updated[index]);
                    }
                  }}
                  onBlur={() => {
                    if (item.id) {
                      saveToServer(leaveTypes[index]);
                    }
                  }}
                  disabled={item.active === false}
                />
                <span className="toggle-slider"></span>
              </label>
              <span className="toggle-label">{t("app.leaveTypes.maxAbsent")}</span>
            </div>

            {/* Max Absent Input - Show when Max Absent is enabled */}
            {item.isMaxAbsent && (
              <div>
                <label className="settings-label">{t("app.leaveTypes.maxAbsentValue")}</label>
                <div className="count-input-group h40 w90">
                  <input
                    type="number"
                    className="count-input"
                    placeholder="0"
                    value={item.maxAbsent && item.maxAbsent > 0 ? item.maxAbsent : ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                      handleChange(index, "maxAbsent", value);
                    }}
                    onBlur={() => {
                      if (item.id) {
                        saveToServer(leaveTypes[index]);
                      }
                    }}
                    min="0"
                    step="0.5"
                    disabled={item.active === false}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Accrual Settings Section - Only show if Allowance is enabled (deductible) */}
          {(item.takeFromAllowance === true || item.deducted === true) && (
            <div className="accrual-section-container">
              <button
                type="button"
                className="accrual-toggle-button"
                onClick={() => toggleAccrualSection(index)}
                disabled={item.active === false}
              >
                <Icon name={expandedAccrualCards[index] ? "chevron down" : "chevron right"} />
                <span>{t("app.leaveTypes.accrualSettings")}</span>
                {item.accrualFrequency && item.accrualFrequency !== null && item.accrualFrequency !== 'Annually' && (
                  <span className="accrual-badge">
                    {getFrequencyDisplayName(item.accrualFrequency)}
                  </span>
                )}
                {(!item.accrualFrequency || item.accrualFrequency === 'Annually') && (
                  <span className="accrual-badge" style={{ background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)" }}>
                    {t("app.leaveTypes.frontLoadedDefault")}
                  </span>
                )}
              </button>

              {expandedAccrualCards[index] && (
                <div className="accrual-settings-panel">
                  {/* Default Accrual Method Indicator - Only show when using default (Annually) */}
                  {(!item.accrualFrequency || item.accrualFrequency === 'Annually') && (
                    <div className="accrual-field-group">
                      <label className="accrual-label">
                        {t("app.leaveTypes.defaultAccrualMethod")}
                        <span
                          className="info-icon"
                          style={{ cursor: "pointer" }}
                          onClick={() => setInfoPopup({ type: 'accrualMethod', index })}
                        >
                          <Icon name="info circle" size="small" />
                        </span>
                      </label>
                      <div style={{
                        padding: "12px",
                        background: "#f0f7ff",
                        borderRadius: "6px",
                        border: "1px solid #b3d9ff"
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <Icon name="check circle" color="green" />
                          <span style={{ fontWeight: "500" }}>{t("app.leaveTypes.frontLoadedAnnualGrant")}</span>
                          <span style={{ fontSize: "0.85em", color: "#666", marginLeft: "auto" }}>{t("app.leaveTypes.default")}</span>
                        </div>
                        <p style={{ margin: "8px 0 0 0", fontSize: "0.9em", color: "#666" }}>
                          {t("app.leaveTypes.fullAmountGrantedOverride")}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Accrual Frequency Selection */}
                  <div className="accrual-field-group">
                    <label className="accrual-label">
                      {t("app.leaveTypes.accrualFrequency")}
                      <span
                        className="info-icon"
                        style={{ cursor: "pointer" }}
                        onClick={() => setInfoPopup({ type: 'accrualMethod', index })}
                      >
                        <Icon name="info circle" size="small" />
                      </span>
                    </label>
                    <select
                      className="accrual-select"
                      value={item.accrualFrequency || 'Annually'}
                      onChange={(e) => {
                        const frequency = e.target.value === 'Annually' ? 'Annually' : e.target.value;
                        const updated = [...leaveTypes];
                        updated[index].accrualFrequency = frequency;

                        // Set enableAccrual: false for Annually (Front-loaded), true for other frequencies
                        if (frequency === 'Annually') {
                          updated[index].enableAccrual = false;
                          // Clear proration and accrual start type for Annually
                          updated[index].enableProration = false;
                          updated[index].accrualStartType = null;
                        } else {
                          updated[index].enableAccrual = true;
                          // Set default values for other frequencies if not already set
                          if (updated[index].enableProration === undefined || updated[index].enableProration === null) {
                            updated[index].enableProration = false;
                          }
                          if (!updated[index].accrualStartType) {
                            updated[index].accrualStartType = ACCRUAL_START_DATE_VALUES.LEAVE_YEAR_START;
                          }
                        }

                        // Handle tiered frequency
                        if (frequency === 'Tiered') {
                          // Initialize tiered rates if not exists
                          if (!updated[index].tieredRates || updated[index].tieredRates.length === 0) {
                            updated[index].tieredRates = [{ years: 0, days: 0, hours: 0 }];
                          }
                          updated[index].accrualRate = 0; // Clear rate for tiered
                        } else {
                          // For other frequencies, ensure tieredRates is cleared
                          updated[index].tieredRates = [];
                          // Initialize accrualRate to 0 if not set
                          if (updated[index].accrualRate === undefined || updated[index].accrualRate === null) {
                            updated[index].accrualRate = 0;
                          }
                        }

                        setLeaveTypes(updated);
                        // Don't save immediately on frequency change - let user configure first
                      }}
                      onBlur={() => {
                        if (item.id) {
                          saveToServer(leaveTypes[index]);
                        }
                      }}
                      disabled={item.active === false}
                    >
                      <option value="Annually">{t("app.leaveTypes.frequencyAnnually")}</option>
                      <option value="Monthly">{t("app.leaveTypes.frequencyMonthly")}</option>
                      <option value="BiWeekly">{t("app.leaveTypes.frequencyBiWeekly")}</option>
                      <option value="Weekly">{t("app.leaveTypes.frequencyWeekly")}</option>
                      <option value="Quarterly">{t("app.leaveTypes.frequencyQuarterly")}</option>
                      <option value="Tiered">{t("app.leaveTypes.frequencyTiered")}</option>
                    </select>
                  </div>

                  {/* Accrual Rate and Maximum Balance - shown for all frequencies except None, Tiered, and Annually */}
                  {item.accrualFrequency && item.accrualFrequency !== null && item.accrualFrequency !== 'Tiered' && item.accrualFrequency !== 'Annually' && (
                    <div className="accrual-field-group">
                      {/* Light border container for Unit, Accrual Rate, and Maximum Balance */}
                      <div style={{
                        padding: "16px",
                        border: "1px solid #dee2e6",
                        borderRadius: "8px",
                        backgroundColor: "#f8f9fa"
                      }}>
                        {/* Shared Days/Hours Radio Buttons - displayed once above both fields */}
                        <div style={{ marginBottom: "16px" }}>
                          <label className="accrual-label" style={{ marginBottom: "10px" }}>
                            {t("app.leaveTypes.unit")}
                          </label>
                          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                            {/* Use shared unit - prioritize rateUnit, then capUnit, then default to Days */}
                            {(() => {
                              const sharedUnit = accrualRateUnits[index] || accrualCapUnits[index] || 'Days';

                              return (
                                <>
                                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <input
                                      type="radio"
                                      name={`accrualUnit-${index}`}
                                      id={`accrualDays-${index}`}
                                      checked={sharedUnit === 'Days'}
                                      onChange={() => {
                                        const newUnit = 'Days';
                                        // Update both units to keep them synchronized
                                        setAccrualRateUnits(prev => ({ ...prev, [index]: newUnit }));
                                        setAccrualCapUnits(prev => ({ ...prev, [index]: newUnit }));
                                        // Save immediately when unit changes (force save to bypass hasChanges check)
                                        // Pass the new division values directly in the leave object
                                        if (item.id) {
                                          const leaveToSave = {
                                            ...leaveTypes[index],
                                            accrualRateDivision: 0,  // Days = 0
                                            accrualCapDivision: 0   // Days = 0
                                          };
                                          setTimeout(() => {
                                            saveToServer(leaveToSave, index, true);
                                          }, 0);
                                        }
                                      }}
                                      disabled={item.active === false}
                                      style={{ cursor: "pointer" }}
                                    />
                                    <label htmlFor={`accrualDays-${index}`} style={{ cursor: "pointer", margin: 0, fontSize: "0.9em", fontWeight: "500" }}>
                                      {t("app.leaveTypes.days")}
                                    </label>
                                  </div>
                                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <input
                                      type="radio"
                                      name={`accrualUnit-${index}`}
                                      id={`accrualHours-${index}`}
                                      checked={sharedUnit === 'Hours'}
                                      onChange={() => {
                                        const newUnit = 'Hours';
                                        // Update both units to keep them synchronized
                                        setAccrualRateUnits(prev => ({ ...prev, [index]: newUnit }));
                                        setAccrualCapUnits(prev => ({ ...prev, [index]: newUnit }));
                                        // Save immediately when unit changes (force save to bypass hasChanges check)
                                        // Pass the new division values directly in the leave object
                                        if (item.id) {
                                          const leaveToSave = {
                                            ...leaveTypes[index],
                                            accrualRateDivision: 1,  // Hours = 1
                                            accrualCapDivision: 1   // Hours = 1
                                          };
                                          setTimeout(() => {
                                            saveToServer(leaveToSave, index, true);
                                          }, 0);
                                        }
                                      }}
                                      disabled={item.active === false}
                                      style={{ cursor: "pointer" }}
                                    />
                                    <label htmlFor={`accrualHours-${index}`} style={{ cursor: "pointer", margin: 0, fontSize: "0.9em", fontWeight: "500" }}>
                                      {t("app.leaveTypes.hours")}
                                    </label>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Accrual Rate and Maximum Balance on same row */}
                        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                          {/* Accrual Rate */}
                          <div style={{ flex: "1", minWidth: "250px" }}>
                            <label className="accrual-label">
                              {t("app.leaveTypes.accrualRate")}
                              <span
                                className="info-icon"
                                style={{ cursor: "pointer" }}
                                onClick={() => setInfoPopup({ type: 'accrualRate', index })}
                              >
                                <Icon name="info circle" size="small" />
                              </span>
                            </label>
                            {(() => {
                              const sharedUnit = accrualRateUnits[index] || accrualCapUnits[index] || 'Days';
                              const rateDisplayValue = sharedUnit === 'Hours' && item.accrualRate > 0
                                ? parseFloat(daysToHours(item.accrualRate))
                                : (item.accrualRate !== undefined && item.accrualRate !== null ? item.accrualRate : '');

                              return (
                                <>
                                  <div className="accrual-input-wrapper">
                                    <input
                                      type="number"
                                      className="accrual-input"
                                      placeholder="0.00"
                                      value={rateDisplayValue}
                                      onChange={(e) => {
                                        const inputValue = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                                        // Convert to days if in hours mode, otherwise use as is
                                        const daysValue = sharedUnit === 'Hours'
                                          ? parseFloat(hoursToDays(inputValue))
                                          : inputValue;
                                        handleChange(index, "accrualRate", daysValue);
                                      }}
                                      onBlur={() => {
                                        if (item.id) {
                                          saveToServer(leaveTypes[index]);
                                        }
                                      }}
                                      min="0"
                                      step={sharedUnit === 'Hours' ? "0.01" : "0.01"}
                                      disabled={item.active === false}
                                    />
                                    <span className="accrual-unit">{sharedUnit}</span>
                                  </div>

                                  {/* Conversion Label - Show when in Hours mode */}
                                  {sharedUnit === 'Hours' && item.accrualRate > 0 && (
                                    <div style={{ marginTop: "8px", padding: "8px", background: "#e7f3ff", borderRadius: "4px", fontSize: "0.85em" }}>
                                      <Icon name="exchange" size="small" style={{ marginRight: "6px" }} />
                                      <span>
                                        <strong>{parseFloat(daysToHours(item.accrualRate))} {t("app.leaveTypes.hours")}</strong> = <strong>{item.accrualRate} {t("app.leaveTypes.days")}</strong>
                                      </span>
                                    </div>
                                  )}

                                  {item.accrualFrequency !== 'Annually' && item.accrualRate > 0 && (
                                    <div className="accrual-preview">
                                      <Icon name="calculator" size="small" />
                                      <span>
                                        {t("app.leaveTypes.annualPreview", { days: calculateAnnualAccrual(item.accrualFrequency, item.accrualRate).toFixed(2), hours: daysToHours(calculateAnnualAccrual(item.accrualFrequency, item.accrualRate)) })}
                                      </span>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                          {/* Maximum Balance (Accrual Cap) */}
                          <div style={{ flex: "1", minWidth: "250px" }}>
                            <label className="accrual-label">
                              {t("app.leaveTypes.maximumBalanceAccrualCap")}
                              <span
                                className="info-icon"
                                style={{ cursor: "pointer" }}
                                onClick={() => setInfoPopup({ type: 'accrualCap', index })}
                              >
                                <Icon name="info circle" size="small" />
                              </span>
                            </label>
                            {(() => {
                              const sharedUnit = accrualRateUnits[index] || accrualCapUnits[index] || 'Days';
                              const capDisplayValue = sharedUnit === 'Hours' && item.accrualCap > 0
                                ? parseFloat(daysToHours(item.accrualCap))
                                : (item.accrualCap || '');

                              return (
                                <>
                                  <div className="accrual-input-wrapper">
                                    <input
                                      type="number"
                                      className="accrual-input"
                                      placeholder={t("app.leaveTypes.placeholderUnlimited")}
                                      value={capDisplayValue}
                                      onChange={(e) => {
                                        const inputValue = parseFloat(e.target.value) || 0;
                                        // Convert to days if in hours mode, otherwise use as is
                                        const daysValue = sharedUnit === 'Hours'
                                          ? parseFloat(hoursToDays(inputValue))
                                          : inputValue;
                                        handleChange(index, "accrualCap", daysValue);
                                      }}
                                      onBlur={() => {
                                        if (item.id) {
                                          saveToServer(leaveTypes[index]);
                                        }
                                      }}
                                      min="0"
                                      step={sharedUnit === 'Hours' ? "0.01" : "0.01"}
                                      disabled={item.active === false}
                                    />
                                    <span className="accrual-unit">{sharedUnit}</span>
                                  </div>

                                  {/* Conversion Label - Show when in Hours mode */}
                                  {sharedUnit === 'Hours' && item.accrualCap > 0 && (
                                    <div style={{ marginTop: "8px", padding: "8px", background: "#e7f3ff", borderRadius: "4px", fontSize: "0.85em" }}>
                                      <Icon name="exchange" size="small" style={{ marginRight: "6px" }} />
                                      <span>
                                        <strong>{parseFloat(daysToHours(item.accrualCap))} {t("app.leaveTypes.hours")}</strong> = <strong>{item.accrualCap} {t("app.leaveTypes.days")}</strong>
                                      </span>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Proration Settings - shown for all accrual frequencies except Annually */}
                  {item.accrualFrequency && item.accrualFrequency !== null && item.accrualFrequency !== 'Annually' && (
                    <div className="accrual-field-group">
                      <label className="accrual-label">
                        {t("app.leaveTypes.proration")}
                        <span className="info-icon" title="Adjust accrual for partial periods (new hires, terminations)">
                          <Icon name="info circle" size="small" />
                        </span>
                      </label>
                      <div className="toggle-switch-item">
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={item.enableProration === true}
                            onChange={(e) =>
                              handleChange(index, "enableProration", e.target.checked)
                            }
                            onBlur={() => {
                              if (item.id) {
                                saveToServer(leaveTypes[index]);
                              }
                            }}
                            disabled={item.active === false}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                        <span className="toggle-label">{t("app.leaveTypes.enableProration")}</span>
                      </div>
                    </div>
                  )}

                  {/* Accrual Start Date Option - shown only when Proration is enabled */}
                  {item.accrualFrequency && item.accrualFrequency !== null && item.accrualFrequency !== 'Annually' && (
                    <div className="accrual-field-group">
                      <label className="accrual-label">
                        {t("app.leaveTypes.accrualStart")}
                        <span className="info-icon" title="When accrual begins for employees">
                          <Icon name="info circle" size="small" />
                        </span>
                      </label>
                      {item.enableProration === true ? (
                        <select
                          className="accrual-select"
                          value={item.accrualStartType || ACCRUAL_START_DATE_VALUES.LEAVE_YEAR_START}
                          onChange={(e) => {
                            handleChange(index, "accrualStartType", e.target.value);
                          }}
                          onBlur={() => {
                            if (item.id) {
                              saveToServer(leaveTypes[index]);
                            }
                          }}
                          disabled={item.active === false}
                        >
                          {ACCRUAL_START_DATE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {t(`app.leaveTypes.accrualStart${option.value}`)}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div style={{
                          padding: "10px 12px",
                          background: "#f8f9fa",
                          border: "1px solid #dee2e6",
                          borderRadius: "6px",
                          fontSize: "13px",
                          color: "#6c757d",
                          fontStyle: "italic"
                        }}>
                          {t("app.leaveTypes.accrualBeginFromLeaveYear")}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tiered Accrual Settings */}
                  {item.accrualFrequency === 'Tiered' && (
                    <div className="tiered-accrual-section">
                      <label className="accrual-label">
                        {t("app.leaveTypes.tieredRates")}
                        <span
                          className="info-icon"
                          style={{ cursor: "pointer" }}
                          onClick={() => setInfoPopup({ type: 'tieredRates', index })}
                        >
                          <Icon name="info circle" size="small" />
                        </span>
                      </label>
                      <p style={{ fontSize: "0.85em", color: "#6c757d", marginBottom: "12px", fontStyle: "italic", marginTop: "-5px" }}>
                        {t("app.leaveTypes.tieredRatesDescription")}
                      </p>
                      <div className="tiered-rates-container">
                        {item.tieredRates && item.tieredRates.length > 0 ? (
                          item.tieredRates.map((tier, tierIndex) => (
                            <div key={tierIndex} className="tier-rate-row">
                              <div className="tier-label-group">
                                <label className="tier-field-label">{t("app.leaveTypes.after")}</label>
                                <input
                                  type="number"
                                  className="tier-input"
                                  placeholder="0"
                                  value={tier.years || ''}
                                  onChange={(e) => {
                                    const updated = [...leaveTypes];
                                    updated[index].tieredRates[tierIndex].years = parseFloat(e.target.value) || 0;
                                    setLeaveTypes(updated);
                                  }}
                                  onBlur={() => {
                                    if (item.id && item.tieredRates[tierIndex]) {
                                      saveTierToServer(item.id, item.tieredRates[tierIndex], tierIndex);
                                    }
                                  }}
                                  min="0"
                                  step="0.5"
                                  disabled={item.active === false}
                                />
                                <span className="tier-unit">{t("app.leaveTypes.years")}</span>
                              </div>
                              <span className="tier-separator">→</span>
                              <div className="tier-label-group">
                                <label className="tier-field-label">{t("app.leaveTypes.grant")}</label>
                                <input
                                  type="number"
                                  className="tier-input"
                                  placeholder="0"
                                  value={tier.days !== undefined && tier.days !== null ? tier.days : ''}
                                  onChange={(e) => {
                                    const daysValue = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                                    const hoursValue = daysValue * 8; // Auto-calculate hours
                                    const updated = [...leaveTypes];
                                    updated[index].tieredRates[tierIndex].days = daysValue;
                                    updated[index].tieredRates[tierIndex].hours = hoursValue;
                                    setLeaveTypes(updated);
                                  }}
                                  onBlur={() => {
                                    if (item.id && item.tieredRates[tierIndex]) {
                                      saveTierToServer(item.id, item.tieredRates[tierIndex], tierIndex);
                                    }
                                  }}
                                  min="0"
                                  step="0.5"
                                  disabled={item.active === false}
                                />
                                <span className="tier-unit">{t("app.leaveTypes.days")}</span>
                              </div>
                              <span className="tier-separator">{t("app.leaveTypes.or")}</span>
                              <div className="tier-label-group">
                                <label className="tier-field-label">{t("app.leaveTypes.grant")}</label>
                                <input
                                  type="number"
                                  className="tier-input"
                                  placeholder="0"
                                  value={tier.hours !== undefined && tier.hours !== null ? tier.hours : ''}
                                  onChange={(e) => {
                                    const hoursValue = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                                    const daysValue = hoursValue / 8; // Auto-calculate days
                                    const updated = [...leaveTypes];
                                    updated[index].tieredRates[tierIndex].hours = hoursValue;
                                    updated[index].tieredRates[tierIndex].days = daysValue;
                                    setLeaveTypes(updated);
                                  }}
                                  onBlur={() => {
                                    if (item.id && item.tieredRates[tierIndex]) {
                                      saveTierToServer(item.id, item.tieredRates[tierIndex], tierIndex);
                                    }
                                  }}
                                  min="0"
                                  step="0.1"
                                  disabled={item.active === false}
                                />
                                <span className="tier-unit">{t("app.leaveTypes.hours")}</span>
                              </div>
                              {item.tieredRates.length > 1 && (
                                <button
                                  type="button"
                                  className="tier-remove-btn"
                                  onClick={async () => {
                                    const updated = [...leaveTypes];
                                    const tier = updated[index].tieredRates[tierIndex];
                                    updated[index].tieredRates.splice(tierIndex, 1);
                                    setLeaveTypes(updated);

                                    // Delete tier from server if it has an ID
                                    if (tier && tier.id) {
                                      await dispatch(onDeleteTier(tier.id));
                                    }
                                  }}
                                  disabled={item.active === false}
                                >
                                  <Icon name="trash" size="small" />
                                </button>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="no-tiers-message">{t("app.leaveTypes.noTiersConfigured")}</div>
                        )}
                        <button
                          type="button"
                          className="add-tier-btn"
                          onClick={() => {
                            const updated = [...leaveTypes];
                            if (!updated[index].tieredRates) {
                              updated[index].tieredRates = [];
                            }
                            updated[index].tieredRates.push({ years: 0, days: 0, hours: 0 });
                            setLeaveTypes(updated);
                          }}
                          disabled={item.active === false}
                        >
                          <Icon name="plus" size="small" />
                          {t("app.leaveTypes.addTier")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleAddRow = () => {
    setLeaveTypes((prev) => {
      const newIndex = prev.length;
      lastAddedIndexRef.current = newIndex;
      return [
        ...prev,
        {
          name: "",
          color: generateRandomColor(),
          takeFromAllowance: false,
          deducted: false, // Ensure deducted is also set
          requiresApproval: false,
          isMaxAbsent: false,
          maxAbsent: 0,
          icon: "no-icon",
          selectOption: "no-icon", // Keep for backward compatibility
          defaultLeaveCount: 0,
          active: true,
          // Accrual defaults - default to Annually (Front-loaded)
          accrualRate: 0,
          accrualFrequency: 'Annually',
          accrualCap: 0,
          enableProration: false,
          accrualStartType: ACCRUAL_START_DATE_VALUES.LEAVE_YEAR_START,
          tieredRates: [],
          enableAccrual: false,
        },
      ];
    });
    // Set flag to scroll to the new leave type after it's rendered
    setShouldScrollToNew(true);
  };

  const { isSaving } = leaveTypeStore;

  return (
    <div className="leave-types-container" style={{ position: 'relative' }}>
      {isSaving && (
        <div
          className="leave-types-saving-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255, 255, 255, 0.85)',
            zIndex: 9999,
          }}
        >
          <FetchingSpinner text={t("app.modals.addLeave.saving")} />
        </div>
      )}
      {/* Header Section */}
      <div className="settings-section">
        <div className="section-header">
          <div className="section-icon">
            <Icon name="calendar alternate outline" className="m-0 mb-1" />
          </div>
          <div className="section-content">
            <h3 className="section-title">{t("app.leaveTypes.title")}</h3>
            <p className="section-description">
              {t("app.leaveTypes.description")}
            </p>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="settings-page-content">
        <div className="content-card">
          {/* Info Section */}
          <div className="info-section">
            <div className="info-icon">
              <Icon name="info circle" />
            </div>
            <div className="info-text">
              <h3>{t("app.leaveTypes.managingLeaveTypes")}</h3>
              <p>
                {t("app.leaveTypes.managingLeaveTypesDescription")}
              </p>
            </div>
          </div>

          {/* Stats Cards - grid layout adapts when Total Allowance is hidden (no leave types with allowance) */}
          {(() => {
            const hasAllowanceCard = leaveTypes?.some((lt) => lt.takeFromAllowance === true || lt.deducted === true);
            return (
          <div
            className={`stats-grid leave-types-stats-grid${!hasAllowanceCard ? " leave-types-stats-grid--3-cards" : ""}`}
          >
            {hasAllowanceCard && (
              <div className="stat-card stat-card-total-allowance">
                <div
                  className="stat-icon"
                  style={{
                    background:
                      "linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)",
                  }}
                >
                  <Icon name="calendar alternate" />
                </div>
                <div className="stat-content">
                  {(() => {
                    const totalDays = leaveTypes?.reduce(
                      (sum, lt) => sum + (Number(lt.defaultLeaveCount) || 0),
                      0
                    ) ?? 0;
                    const totalHours = daysToHours(totalDays);
                    return (
                      <>
                        <div className="stat-value-block">
                          <h3 className="stat-number">{totalDays}</h3>
                          <span className="stat-number-subtext">{totalHours} {t("app.leaveTypes.hours")}</span>
                        </div>
                        <p className="stat-label">{t("app.leaveTypes.totalAllowance")}</p>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            <div className="stat-card">
              <div
                className="stat-icon"
                style={{
                  background:
                    "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                }}
              >
                <Icon name="calendar check" />
              </div>
              <div className="stat-content">
                <h3 className="stat-number">{leaveTypes?.length || 0}</h3>
                <p className="stat-label">{t("app.leaveTypes.totalTypes")}</p>
              </div>
            </div>

            <div className="stat-card">
              <div
                className="stat-icon"
                style={{
                  background:
                    "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                }}
              >
                <Icon name="shield alternate" />
              </div>
              <div className="stat-content">
                <h3 className="stat-number">
                  {leaveTypes?.filter((lt) => lt.active !== false).length || 0}
                </h3>
                <p className="stat-label">{t("app.leaveTypes.activeTypes")}</p>
              </div>
            </div>

            <div className="stat-card">
              <div
                className="stat-icon"
                style={{
                  background:
                    "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                }}
              >
                <Icon name="clock outline" />
              </div>
              <div className="stat-content">
                <h3 className="stat-number">
                  {leaveTypes?.filter((lt) => lt.requiresApproval).length || 0}
                </h3>
                <p className="stat-label">{t("app.leaveTypes.requireApproval")}</p>
              </div>
            </div>

          </div>
            );
          })()}

          {/* Add New Button */}
          <div className="add-section">
            <button className="add-button" onClick={handleAddRow}>
              <Icon name="plus circle" />
              {t("app.leaveTypes.addNewLeaveType")}
            </button>
          </div>

          {/* Leave Types List */}
          <div className="leave-types-list">
            {leaveTypes && leaveTypes.length > 0 ? (
              <div className="leave-types-grid">
                {leaveTypes.map((item, index) => renderLeaves(item, index))}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">
                  <Icon name="calendar x" size="huge" />
                </div>
                <h3>{t("app.leaveTypes.noLeaveTypesYet")}</h3>
                <p>
                  Create your first leave type to get started with employee time
                  off management
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Info Popup Modal */}
      <Modal
        open={infoPopup.type !== null}
        onClose={() => setInfoPopup({ type: null, index: null })}
        size="small"
        closeIcon
      >
        <ModalHeader>
          <Icon name="info circle" /> {t("app.leaveTypes.information")}
        </ModalHeader>
        <ModalContent>
          {infoPopup.type === 'accrualMethod' && (
            <div>
              <h4>{t("app.leaveTypes.infoAccrualFrequencyTitle")}</h4>
              <p>{t("app.leaveTypes.infoAccrualFrequencyIntro")}</p>
              <div style={{ marginTop: "15px" }}>
                <strong>Default Method:</strong>
                <div style={{ marginTop: "8px", padding: "10px", background: "#f0f7ff", borderRadius: "6px" }}>
                  <strong>Front-loaded (Annual Grant) - Default:</strong> Full amount granted at the start of the year. <em>Example: 20 days granted on January 1st.</em>
                </div>
                <div style={{ marginTop: "15px" }}>
                  <strong>Available Frequencies:</strong>
                  <ul style={{ marginTop: "10px", paddingLeft: "20px" }}>
                    <li><strong>Monthly:</strong> Leave accrues each month. <em>Example: 1.25 days/month = 15 days/year.</em></li>
                    <li><strong>Bi-weekly:</strong> Leave accrues every two weeks. <em>Example: 0.58 days/bi-week = 15 days/year.</em></li>
                    <li><strong>Weekly:</strong> Leave accrues every week. <em>Example: 0.29 days/week = 15 days/year.</em></li>
                    <li><strong>Quarterly:</strong> Leave accrues every 3 months. <em>Example: 3.75 days/quarter = 15 days/year.</em></li>
                    <li><strong>Tiered (Based on Tenure):</strong> Different rates based on years of service. <em>Example: 0-2 years = 10 days/year, 3-5 years = 15 days/year, 5+ years = 20 days/year.</em></li>
                  </ul>
                </div>
                <p style={{ marginTop: "15px", fontStyle: "italic", color: "#666" }}>
                  <strong>Note:</strong> If you don't select a frequency, the system uses Front-loaded (Annual Grant) as the default.
                </p>
              </div>
            </div>
          )}

          {infoPopup.type === 'tieredRates' && (
            <div>
              <h4>Tiered Rates</h4>
              <p>Configure different leave accrual rates based on employee years of service. Employees automatically move to higher tiers as their tenure increases.</p>
              <div style={{ marginTop: "15px" }}>
                <strong>How it works:</strong>
                <ul style={{ marginTop: "10px", paddingLeft: "20px" }}>
                  <li>Each tier defines a <strong>years threshold</strong> and <strong>days per year</strong></li>
                  <li>The system finds the highest tier that the employee's years of service meets or exceeds</li>
                  <li>Employees automatically get the appropriate tier rate based on their hire date</li>
                </ul>
                <div style={{ marginTop: "15px", padding: "12px", background: "#f0f7ff", borderRadius: "6px" }}>
                  <strong>Example Configuration:</strong>
                  <div style={{ marginTop: "10px" }}>
                    <p style={{ margin: "5px 0" }}><strong>Tier 1:</strong> 0 years → 10 days/year</p>
                    <p style={{ margin: "5px 0" }}><strong>Tier 2:</strong> 3 years → 15 days/year</p>
                    <p style={{ margin: "5px 0" }}><strong>Tier 3:</strong> 5 years → 20 days/year</p>
                  </div>
                  <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #b3d9ff" }}>
                    <strong>Employee Examples:</strong>
                    <ul style={{ marginTop: "8px", paddingLeft: "20px" }}>
                      <li><strong>New hire (0 years):</strong> Gets 10 days/year (Tier 1)</li>
                      <li><strong>3 years service:</strong> Gets 15 days/year (Tier 2)</li>
                      <li><strong>4 years service:</strong> Gets 15 days/year (Tier 2)</li>
                      <li><strong>6 years service:</strong> Gets 20 days/year (Tier 3)</li>
                    </ul>
                  </div>
                </div>
                <p style={{ marginTop: "15px", fontStyle: "italic", color: "#666" }}>
                  <strong>Tip:</strong> Tiers should be ordered from lowest to highest years. The system automatically applies the highest tier that the employee qualifies for.
                </p>
              </div>
            </div>
          )}

          {infoPopup.type === 'accrualRate' && (
            <div>
              <h4>Accrual Rate</h4>
              <p>The amount of leave earned per accrual period.</p>
              <div style={{ marginTop: "15px" }}>
                <strong>Examples:</strong>
                <ul style={{ marginTop: "10px", paddingLeft: "20px" }}>
                  <li><strong>Monthly Method:</strong> Enter <strong>1.25 days</strong> to give employees 15 days per year (1.25 × 12 months = 15 days)</li>
                  <li><strong>Bi-weekly Method:</strong> Enter <strong>0.58 days</strong> to give employees 15 days per year (0.58 × 26 pay periods = 15.08 days)</li>
                  <li><strong>Weekly Method:</strong> Enter <strong>0.29 days</strong> to give employees 15 days per year (0.29 × 52 weeks = 15.08 days)</li>
                  <li><strong>Quarterly Method:</strong> Enter <strong>3.75 days</strong> to give employees 15 days per year (3.75 × 4 quarters = 15 days)</li>
                  <li><strong>Annually (Front-loaded):</strong> Enter <strong>20 days</strong> to grant 20 days at the start of the year (this is the default method)</li>
                </ul>
                <p style={{ marginTop: "15px", fontStyle: "italic", color: "#666" }}>
                  <strong>Note:</strong> The system automatically calculates the annual total based on your selected method and rate. Hours are calculated at 8 hours per day.
                </p>
              </div>
            </div>
          )}

          {infoPopup.type === 'accrualCap' && (
            <div>
              <h4>Maximum Balance (Accrual Cap)</h4>
              <p>Sets the maximum amount of leave an employee can accrue. Once reached, accrual stops until leave is used.</p>
              <div style={{ marginTop: "15px" }}>
                <strong>How it works:</strong>
                <ul style={{ marginTop: "10px", paddingLeft: "20px" }}>
                  <li><strong>0 = Unlimited:</strong> No cap - employees can accrue leave indefinitely</li>
                  <li><strong>Fixed Cap (e.g., 20 days):</strong> Once an employee reaches 20 days, accrual stops</li>
                </ul>
                <div style={{ marginTop: "15px", padding: "12px", background: "#f0f7ff", borderRadius: "6px" }}>
                  <strong>Example Scenario:</strong>
                  <p style={{ margin: "8px 0" }}>
                    <strong>Settings:</strong> Monthly accrual of 1.25 days, Maximum Balance = 20 days
                  </p>
                  <p style={{ margin: "8px 0" }}>
                    <strong>Timeline:</strong>
                  </p>
                  <ul style={{ margin: "5px 0", paddingLeft: "20px" }}>
                    <li>Month 1-16: Employee accrues normally (reaches 20 days by month 16)</li>
                    <li>Month 17+: Accrual stops at 20 days cap</li>
                    <li>When employee uses 5 days: Balance drops to 15 days, accrual resumes</li>
                    <li>Accrual continues until cap is reached again</li>
                  </ul>
                </div>
                <p style={{ marginTop: "15px", fontStyle: "italic", color: "#666" }}>
                  <strong>Benefits:</strong> Prevents excessive leave accumulation, encourages leave usage, and helps manage financial liability.
                </p>
              </div>
            </div>
          )}
        </ModalContent>
        <ModalActions>
          <Button onClick={() => setInfoPopup({ type: null, index: null })} positive>
            {t("app.leaveTypes.gotIt")}
          </Button>
        </ModalActions>
      </Modal>
    </div>
  );
};

export default LeaveType;
